import { create } from 'zustand';
import { http } from '@/lib/http';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const parsePreferenceValue = (raw) => {
  if (raw === null) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
  return raw;
};

// ─── Store ───────────────────────────────────────────────────────────────────

const store = create(() => ({
  preferences: {},
  isLoading: false,
  isLoaded: false,
}));

// ─── React hooks ─────────────────────────────────────────────────────────────

export const usePreferences = () => store((state) => state.preferences);
export const useIsPreferencesLoading = () => store((state) => state.isLoading);
export const useIsPreferencesLoaded = () => store((state) => state.isLoaded);

// ─── Server API methods  ───────────────────────────────────────────

const fetchAllFromServer = async (prefetched) => {
  const res = await (prefetched ?? http.get('/user_preference'));
  const raw = res?.data?.preferences ?? {};
  return Object.fromEntries(
    Object.entries(raw).map(([key, value]) => [
      key,
      parsePreferenceValue(value),
    ])
  );
};

const saveItemInServer = (key, value) => {
  return http
    .patch('/user_preference', {
      user_preference: { [key]: value !== null ? JSON.stringify(value) : null },
    })
    .catch((err) => console.error('Failed to save preference', key, err));
};

const removeItemInServer = (key) => {
  return http
    .patch('/user_preference', {
      user_preference: { [key]: null },
    })
    .catch((err) => console.error('Failed to remove preference', key, err));
};

// ─── Debounced server sync ───────────────────────────────────────────────────

// Preferences are written on every filter, sort, search and view change, each
// one a PATCH on the critical path of the interaction. The local store still
// updates synchronously; only the network write waits for the user to settle.
const SAVE_DEBOUNCE_MS = 1000;
const pendingSaves = new Map();

const cancelPendingSave = (key) => {
  const timer = pendingSaves.get(key);
  if (timer === undefined) return;
  clearTimeout(timer);
  pendingSaves.delete(key);
};

const scheduleSave = (key, value) => {
  cancelPendingSave(key);
  pendingSaves.set(
    key,
    setTimeout(() => {
      pendingSaves.delete(key);
      saveItemInServer(key, value);
    }, SAVE_DEBOUNCE_MS)
  );
};

/** Writes every queued preference immediately. */
export const flushPreferenceSaves = () => {
  const queued = [...pendingSaves.keys()];
  queued.forEach((key) => {
    cancelPendingSave(key);
    saveItemInServer(key, store.getState().preferences[key]);
  });
  return queued;
};

// A queued write must not be lost to a tab close or a background tab.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushPreferenceSaves);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushPreferenceSaves();
  });
}

// Consumers only call these. Server sync is handled internally.

/** @param prefetched the same GET, already started during boot (`earlyRequests`). */
export const fetchAllUserPreferences = async (prefetched) => {
  store.setState({ isLoading: true });
  try {
    const parsed = await fetchAllFromServer(prefetched);
    store.setState({ preferences: parsed, isLoaded: true });
  } catch (error) {
    console.error('Error fetching user preferences', error);
    store.setState({ isLoaded: true });
  } finally {
    store.setState({ isLoading: false });
  }
};

export const getItem = (key) => {
  return store.getState().preferences[key] ?? null;
};

export const getPreferences = () => store.getState().preferences;

export const setItem = (key, value) => {
  store.setState((state) => ({
    preferences: { ...state.preferences, [key]: value },
  }));
  scheduleSave(key, value);
};

export const updateItem = (key, value) => {
  store.setState((state) => ({
    preferences: { ...state.preferences, [key]: value },
  }));
  scheduleSave(key, value);
};

export const removeItem = (key) => {
  store.setState((state) => ({
    preferences: { ...state.preferences, [key]: null },
  }));
  // Drop any queued write first, or it would land after the removal.
  cancelPendingSave(key);
  removeItemInServer(key);
};
